import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink,
  Pencil,
  Check,
  X,
  Truck,
  ShoppingBag,
  RotateCcw,
  Link2,
  Plus,
  Package,
  AlertTriangle,
  FileText,
  Copy,
  Trash2,
  ShieldCheck,
  ShieldX,
  MessageCircle,
  ArrowDownUp,
  Ban,
} from 'lucide-react';
import {
  UnifiedOrder,
  isDelayedShipment,
  OrderItem,
  Purchase,
  InternalPurchase,
  Shipment,
  Claim,
  AMZ_STATUS_COLORS,
  ML_STATUS_COLORS,
  STOCK_STATUS_COLORS,
} from './types';
import { useUIStore } from '../../stores/uiStore';
import { BagBadge } from '../../components/BagBadge';
import { PurchaseTracker } from '../../components/shared/PurchaseTracker';
import { fetchShippingOptions, type ShippingOption } from './actions';
import { Can, useCan } from '../../lib/rbac';
import { PriceWatchButton } from './PriceWatchButton';
import { ndClaimActions } from './ndClaimView';

/* ── Helpers ─────────────────────────────────────────────── */

function formatCurrency(amount: number | null | undefined, currency = 'USD'): string {
  if (amount == null) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="text-[var(--color-text-secondary)]">{label}</span>
    </span>
  );
}

function LinkSourceBadge({ source }: { source: string | null }) {
  if (!source) return null;
  const colorMap: Record<string, string> = {
    api: 'var(--color-primary)',
    stock: 'var(--color-success)',
    manual: 'var(--color-warning)',
    linked_manual: 'var(--color-warning)',
  };
  return (
    <span
      className="text-[10px] font-medium px-1.5 py-0.5 rounded"
      style={{
        backgroundColor: `color-mix(in srgb, ${colorMap[source] || 'var(--color-text-muted)'} 15%, transparent)`,
        color: colorMap[source] || 'var(--color-text-muted)',
      }}
    >
      {source}
    </span>
  );
}

/* ── InlineEdit ──────────────────────────────────────────── */

interface InlineEditProps {
  value: string;
  onSave: (newValue: string) => void;
  mono?: boolean;
  placeholder?: string;
}

function InlineEdit({ value, onSave, mono = false, placeholder }: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleSave = useCallback(() => {
    if (draft.trim() !== value) {
      onSave(draft.trim());
    }
    setEditing(false);
  }, [draft, value, onSave]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') handleCancel();
    },
    [handleSave, handleCancel]
  );

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`px-1.5 py-0.5 text-xs border border-[var(--color-primary)] rounded bg-[var(--color-bg)] text-[var(--color-text)] outline-none w-28 ${mono ? 'font-mono' : ''}`}
          placeholder={placeholder}
        />
        <button
          onClick={handleSave}
          className="p-0.5 rounded hover:bg-[var(--color-surface)] text-[var(--color-success)]"
        >
          <Check size={12} />
        </button>
        <button
          onClick={handleCancel}
          className="p-0.5 rounded hover:bg-[var(--color-surface)] text-[var(--color-error)]"
        >
          <X size={12} />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 group">
      <span className={`text-xs ${mono ? 'font-mono' : ''} text-[var(--color-text)]`}>
        {value || <span className="text-[var(--color-text-muted)] italic">{placeholder || 'empty'}</span>}
      </span>
      <button
        onClick={() => setEditing(true)}
        className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-[var(--color-surface)] text-[var(--color-text-muted)] transition-opacity"
      >
        <Pencil size={10} />
      </button>
    </span>
  );
}

/* ── Zone 1: Items ───────────────────────────────────────── */

function ItemsZone({ items, orderId }: { items: OrderItem[]; orderId?: number }) {
  const { t } = useTranslation();
  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        {t('orders.accordion.items', 'Items')}
      </h4>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex gap-3">
            <img
              src={item.thumbnail || ''}
              alt=""
              className="w-[120px] h-[120px] rounded-[var(--border-radius)] object-cover bg-[var(--color-surface)] flex-shrink-0"
              loading="lazy"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm text-[var(--color-text)] leading-snug">
                {item.title || '--'}
              </p>
              {item.seller_sku && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  ASIN: <a href={`https://www.amazon.com/dp/${item.seller_sku}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[var(--color-primary)] hover:underline">{item.seller_sku}</a>
                </p>
              )}
              <p className="text-xs text-[var(--color-text-muted)]">
                ID: {item.permalink ? (
                  <a
                    href={item.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
                    title={t('orders.accordion.openListing', 'Abrir publicación en Mercado Libre')}
                  >
                    {item.item_id}
                    <ExternalLink size={10} className="opacity-60" />
                  </a>
                ) : (
                  <span className="font-mono">{item.item_id}</span>
                )}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] inline-flex items-center gap-1">
                Order: <button
                  onClick={() => { navigator.clipboard.writeText(String(orderId || '')); }}
                  className="font-mono text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
                  title="Copy Order ID"
                >
                  {orderId}
                  <Copy size={10} className="opacity-40" />
                </button>
              </p>
              {item.gtin && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  GTIN: <span className="font-mono text-[var(--color-text)]">{item.gtin}</span>
                </p>
              )}
              <p className="text-sm text-[var(--color-text)]">
                <span className={item.quantity > 1 ? 'text-[var(--color-error)] font-bold bg-[var(--color-error-light)] px-2 py-0.5 rounded' : 'text-[var(--color-text-muted)]'}>x{item.quantity}</span>
                {' '}
                <Can
                  cap="global.view_money"
                  fallback={<span className="text-[var(--color-text-muted)]">—</span>}
                >
                  <span className="font-medium">
                    {formatCurrency(item.unit_price, item.currency_id)}
                  </span>
                </Can>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Zone 2: Source (Amazon + Internal Stock) ────────────── */

/* ── Zone 2a: Internal Stock ─────────────────────────────── */

function StockZone({
  internalPurchases,
  order,
  onAction,
}: {
  internalPurchases: InternalPurchase[];
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const can = useCan();
  if (internalPurchases.length === 0) return null;

  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        Stock Interno
      </h4>
      <div className="space-y-2">
        {internalPurchases.map((ip) => {
          const statusColor = STOCK_STATUS_COLORS[ip.status] || 'var(--color-text-muted)';
          return (
            <div
              key={ip.id}
              className="p-3 rounded-[var(--border-radius)] border border-[var(--color-border-light)] bg-[var(--color-bg)] space-y-2"
            >
              {/* Status + badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusDot color={statusColor} label={ip.status} />
                  <span className="text-[10px] text-[var(--color-text-muted)]">qty: {ip.quantity}</span>
                </div>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--color-success) 15%, transparent)',
                    color: 'var(--color-success)',
                  }}
                >
                  Stock
                </span>
              </div>

              {/* ASIN */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text-muted)] w-10">ASIN</span>
                <a
                  href={`https://www.amazon.com/dp/${ip.asin}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-mono text-[var(--color-primary)] hover:underline"
                >
                  {ip.asin}
                </a>
              </div>

              {/* Internal SKU */}
              {ip.internal_sku && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-text-muted)] w-10">SKU</span>
                  <span className="text-xs font-mono text-[var(--color-text)]">{ip.internal_sku}</span>
                </div>
              )}

              {/* Cost — editable for hold status */}
              <Can cap="global.view_money">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-text-muted)] w-10">Cost</span>
                  {ip.status === 'hold' && can('orders.edit_cost') ? (
                    <InlineEdit
                      value={ip.unit_cost != null ? String(ip.unit_cost) : ''}
                      placeholder="0.00"
                      onSave={(val) =>
                        onAction('edit_stock_cost', order, { purchaseId: ip.id, cost: parseFloat(val) })
                      }
                    />
                  ) : (
                    <span className="text-xs text-[var(--color-text)]">{formatCurrency(ip.unit_cost)}</span>
                  )}
                  {ip.quantity > 1 && (
                    <span className="text-xs text-[var(--color-text-muted)]">
                      × {ip.quantity} = {formatCurrency(ip.total_cost)}
                    </span>
                  )}
                </div>
              </Can>

              {/* Total + profit */}
              <Can cap="global.view_money">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[var(--color-text-muted)]">
                    Total: <span className="text-[var(--color-text)]">{formatCurrency(ip.total_cost)}</span>
                  </span>
                  {ip.gross_profit != null && (
                    <span
                      className={
                        ip.gross_profit >= 0
                          ? 'text-[var(--color-success)] font-medium'
                          : 'text-[var(--color-error)] font-medium'
                      }
                    >
                      {formatCurrency(ip.gross_profit)}
                      {ip.profit_margin != null && ` (${ip.profit_margin.toFixed(1)}%)`}
                    </span>
                  )}
                </div>
              </Can>

              {/* Source info: cost layer / SPO / supplier */}
              {(ip.cost_layer_ref || ip.supplier_name || ip.stock_source_type) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {ip.cost_layer_ref && (
                    <div className="text-xs text-[var(--color-primary)] bg-[var(--color-primary-light)] rounded px-2 py-1 inline-flex items-center gap-1">
                      <FileText size={11} />
                      {ip.cost_layer_ref}
                    </div>
                  )}
                  {ip.supplier_name && (
                    <span className="text-[10px] text-[var(--color-text-muted)]">
                      {ip.supplier_name}
                    </span>
                  )}
                  {!ip.cost_layer_ref && ip.stock_source_type === 'internal' && (
                    <div className="text-xs text-[var(--color-info)] bg-[var(--color-info-light,rgba(59,130,246,0.1))] rounded px-2 py-1 inline-flex items-center gap-1">
                      <Package size={11} />
                      Carga inicial
                    </div>
                  )}
                </div>
              )}

              {/* Actions: Release to Amazon (only for hold) */}
              {ip.status === 'hold' && (
                <Can cap="orders.operate">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction('release_stock', order, { purchaseId: ip.id });
                    }}
                    className="text-[10px] px-2 py-1 rounded border border-[var(--color-warning)] text-[var(--color-warning)] hover:bg-[var(--color-warning)] hover:text-white transition-colors"
                  >
                    Release → Amazon
                  </button>
                </Can>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Zone 2b: Amazon Purchases ───────────────────────────── */

function AmazonZone({
  purchases,
  order,
  onAction,
}: {
  purchases: Purchase[];
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  if (purchases.length === 0) return null;
  return <PurchasesSectionInner purchases={purchases} order={order} onAction={onAction} />;
}

/* Picker de opciones de envío en vivo (Amazon) — para compras frenadas por
   entrega lenta. Elegir → ver ganancia → "Comprar con este envío". */
function ShippingOptionsPicker({
  purchase,
  order,
  onAction,
}: {
  purchase: Purchase;
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchShippingOptions>> | null>(null);
  const [selected, setSelected] = useState<ShippingOption | null>(null);
  const can = useCan();
  // El picker existe para comprar eligiendo envío: sin la llave de compra no se ofrece.
  const canBuy = can('orders.buy');
  const canMoney = can('global.view_money');

  const load = async () => {
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      setData(await fetchShippingOptions(purchase.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar opciones');
    } finally {
      setLoading(false);
    }
  };

  if (!canBuy) return null;

  if (!open) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
          load();
        }}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
        title="Ver opciones de envío en vivo y comprar con la elegida"
      >
        <Truck className="w-3 h-3" />
        Opciones de envío
      </button>
    );
  }

  return (
    <div
      className="space-y-1.5 border-t border-[var(--color-border-light)] pt-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase">
          Opciones de envío
          {data?.maxDeliveryDays != null && (
            <span className="normal-case font-normal"> · máx {data.maxDeliveryDays} días</span>
          )}
        </span>
        <button
          onClick={load}
          className="text-[10px] text-[var(--color-primary)] hover:underline"
        >
          Recargar
        </button>
      </div>
      {loading && (
        <div className="text-xs text-[var(--color-text-muted)]">Consultando Amazon…</div>
      )}
      {error && <div className="text-xs text-[var(--color-error)]">{error}</div>}
      {data && data.options.length === 0 && (
        <div className="text-xs text-[var(--color-text-muted)]">Sin ofertas disponibles ahora.</div>
      )}
      {!loading &&
        data?.options.map((opt) => {
          const isSel =
            selected?.offerId === opt.offerId && selected?.shippingCost === opt.shippingCost;
          return (
            <label
              key={`${opt.offerId}-${opt.shippingCost}`}
              className={`flex items-center justify-between gap-2 p-1.5 rounded cursor-pointer border text-xs ${
                isSel
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                  : 'border-transparent hover:bg-[var(--color-bg-secondary)]'
              }`}
            >
              <input
                type="radio"
                className="hidden"
                checked={isSel}
                onChange={() => setSelected(opt)}
              />
              <span className="flex-1 truncate" title={opt.sellerName}>
                {opt.sellerName}
                {canMoney
                  ? ` · $${opt.price.toFixed(2)}${
                      opt.shippingCost > 0
                        ? ` + envío $${opt.shippingCost.toFixed(2)}`
                        : ' + envío gratis'
                    }`
                  : opt.shippingCost > 0
                    ? ' · con costo de envío'
                    : ' · envío gratis'}
              </span>
              <span
                className={
                  opt.onTime ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'
                }
                title={opt.onTime ? 'Llega a tiempo' : 'Llega tarde'}
              >
                {opt.deliveryDays}d
              </span>
              <span
                className={
                  opt.estProfit >= 0
                    ? 'text-[var(--color-success)] font-medium'
                    : 'text-[var(--color-error)] font-medium'
                }
              >
                {canMoney ? `$${opt.estProfit.toFixed(2)}` : '—'}
              </span>
            </label>
          );
        })}
      {selected && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs">
            Ganancia estimada:{' '}
            <span
              className={
                selected.estProfit >= 0
                  ? 'text-[var(--color-success)] font-semibold'
                  : 'text-[var(--color-error)] font-semibold'
              }
            >
              {canMoney ? `$${selected.estProfit.toFixed(2)}` : '—'}
            </span>
            {!selected.onTime && (
              <span className="text-[var(--color-error)] ml-1">(llega tarde)</span>
            )}
          </span>
          <button
            onClick={() => {
              if (
                !confirm(
                  `¿Comprar a ${selected.sellerName} con envío ${
                    selected.shippingCost > 0
                      ? canMoney
                        ? `de $${selected.shippingCost.toFixed(2)}`
                        : 'pago'
                      : 'gratis'
                  } (llega en ~${selected.deliveryDays} días)?`
                )
              )
                return;
              onAction('approve_with_shipping', order, {
                purchaseId: purchase.id,
                offerId: selected.offerId,
                maxShippingCost: selected.shippingCost,
                allowLate: !selected.onTime,
                sellerName: selected.sellerName,
                price: selected.price,
              });
            }}
            className="px-2 py-1 bg-[var(--color-primary)] hover:opacity-90 text-white rounded text-[10px] font-medium transition-colors"
          >
            Comprar con este envío
          </button>
        </div>
      )}
    </div>
  );
}

/** Precio nuevo que la guarda escribió en la leyenda ("… → ahora $74.99 …" o
 *  "… aceptaste $74.99 → ahora $79.99 …"). Null si no se puede leer. */
export function parsePriceJumpNewPrice(message: string | null | undefined): number | null {
  const m = /ahora \$([\d,]+(?:\.\d{1,2})?)/.exec(message || '');
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* Compra retenida por suba de precio al ejecutar (safeguard_hold + PRICE_JUMP_AT_EXECUTE). */
function PriceJumpHoldActions({
  purchase,
  order,
  onAction,
}: {
  purchase: Purchase;
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const newPrice = parsePriceJumpNewPrice(purchase.error_message);
  const quotaHold = order.shipments?.some(s => s.substatus === 'receiver_insufficient_quota');
  if (quotaHold) return null;
  return (
    <Can cap="orders.buy">
      <div className="p-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/25 dark:border-amber-800 space-y-1.5">
        <p className="text-[11px] text-amber-800 dark:text-amber-300 font-semibold">
          Retenida: el precio de Amazon subió al ejecutar.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {newPrice != null && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (
                  !confirm(
                    `¿Comprar igual a ${canMoney ? `$${newPrice.toFixed(2)}` : 'el precio nuevo'} por unidad?\n\n` +
                      `Se vuelve a consultar Amazon. Si el precio está igual o más barato, se compra. ` +
                      `Si subió de nuevo, se retiene otra vez y te lo muestra.`
                  )
                )
                  return;
                onAction('approve_accept_price', order, {
                  purchaseId: purchase.id,
                  acceptPriceUpTo: newPrice,
                });
              }}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-green-600 hover:bg-green-700 text-white transition-colors"
              title="Aceptar el precio nuevo y comprar (nunca más caro que ese precio)"
            >
              <ShoppingBag size={12} />
              Comprar igual{canMoney ? ` a $${newPrice.toFixed(2)}` : ''}
            </button>
          )}
          <ShippingOptionsPicker purchase={purchase} order={order} onAction={onAction} />
        </div>
      </div>
    </Can>
  );
}

function PurchasesSectionInner({
  purchases,
  order,
  onAction,
}: {
  purchases: Purchase[];
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const can = useCan();

  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        Amazon
      </h4>
      <div className="space-y-2">
        {purchases.map((p) => {
          const statusColor = AMZ_STATUS_COLORS[p.amazon_status] || 'var(--color-text-muted)';
          return (
            <div
              key={p.id}
              className="p-3 rounded-[var(--border-radius)] border border-[var(--color-border-light)] bg-[var(--color-bg)] space-y-2"
            >
              {/* Status + ETD + source */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusDot color={statusColor} label={p.amazon_status} />
                  {p.delivery_date && (
                    <span className="text-[10px] text-[var(--color-info)]" title={p.offer_delivery_text || undefined}>
                      ETD {p.offer_delivery_text && ['pending_approval','safeguard_hold'].includes(p.amazon_status)
                        ? p.offer_delivery_text.replace(/^FREE delivery\s*/i, '')
                        : new Date(p.delivery_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <LinkSourceBadge source={p.link_source} />
                  {(() => {
                    // Compras no concretadas se ELIMINAN (borrado real); las
                    // concretadas se DESCARTAN (backend conserva registro +
                    // seguimiento del reembolso y libera la venta).
                    const hardDelete = [
                      'pending_approval',
                      'safeguard_hold',
                      'error',
                      'cancelled',
                      'linked_manual',
                    ].includes(p.amazon_status);
                    const msg = hardDelete
                      ? '¿Eliminar esta compra? Se descontará de finanzas y stock, y la orden quedará libre para re-comprar.'
                      : '¿Descartar esta compra? La venta queda libre para vincular otra compra. El registro y el seguimiento del reembolso se conservan.';
                    return (
                      <Can cap="orders.delete_purchase">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!confirm(msg)) return;
                            onAction('delete_one_purchase', order, { purchaseId: p.id });
                          }}
                          title={hardDelete ? 'Eliminar esta compra' : 'Descartar esta compra'}
                          className="p-0.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </Can>
                    );
                  })()}
                </div>
              </div>

              {/* Amazon Order ID + Cancel */}
              {p.amazon_order_id && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-text-muted)] w-10">Order</span>
                  <span className="text-xs font-mono text-[var(--color-text)]">{p.amazon_order_id}</span>
                  {!['cancelled', 'delivered', 'delivered_ml'].includes(p.amazon_status) && (
                    <Can cap="orders.cancel_purchase">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!confirm(`Cancel Amazon purchase ${p.amazon_order_id}?\n\nThis will:\n1. Mark as cancelled in our system\n2. Open Amazon to cancel the order there too\n\nAmazon API does not support auto-cancel.`)) return;
                        onAction('cancel_purchase', order, { purchaseId: p.id });
                        if (p.amazon_order_id) {
                          window.open(`https://www.amazon.com/gp/css/summary/edit.html?orderID=${p.amazon_order_id}`, '_blank');
                        }
                      }}
                      className="px-1.5 py-0.5 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-medium transition-colors"
                      title="Cancel in our system + open Amazon to cancel there"
                    >
                      Cancel
                    </button>
                    </Can>
                  )}
                </div>
              )}

              {/* En revisión: Amazon sustituyó la oferta al ordenar (precio y/o entrega distintos).
                  La compra YA está hecha. Confirmar = aceptarla tal como quedó; Recomprar = volver a
                  ejecutar la compra (la orden Amazon original hay que cancelarla a mano). */}
              {p.amazon_status === 'needs_review' && (
                <div className="p-2 rounded border border-orange-300 bg-orange-50 dark:bg-orange-950/25 dark:border-orange-800 space-y-1.5">
                  <p className="text-[11px] text-orange-800 dark:text-orange-300">
                    <span className="font-semibold">Amazon cambió la oferta al comprar.</span>{' '}
                    {(p.error_message || '').replace(/^Offer substituted:\s*/i, '') || 'Revisar precio y fecha de entrega.'}
                  </p>
                  <Can cap="orders.buy">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!confirm('¿Confirmar esta compra tal como quedó en Amazon (precio y entrega nuevos)?')) return;
                          onAction('confirm_review', order, { purchaseId: p.id });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-green-600 hover:bg-green-700 text-white transition-colors"
                        title="Aceptar la compra como quedó → pasa a comprada"
                      >
                        <Check size={12} /> Confirmar
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!confirm(`¿Recomprar en Amazon?\n\nSe vuelve a ejecutar la compra. La orden Amazon original (${p.amazon_order_id || 'sin número'}) hay que cancelarla a mano en Amazon.`)) return;
                          onAction('rebuy_review', order, { purchaseId: p.id });
                          if (p.amazon_order_id) {
                            window.open(`https://www.amazon.com/gp/css/summary/edit.html?orderID=${p.amazon_order_id}`, '_blank');
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                        title="Volver a ejecutar la compra + abrir Amazon para cancelar la original"
                      >
                        <RotateCcw size={12} /> Recomprar
                      </button>
                    </div>
                  </Can>
                </div>
              )}

              {/* Retenida porque el precio de Amazon subió al ejecutar (guarda de precio).
                  "Comprar igual" = aceptar comprar HASTA el precio que muestra la leyenda;
                  el backend vuelve a frenar si subió de nuevo. También se ofrece el selector
                  de envío para elegir otra oferta (2026-10-13). */}
              {p.amazon_status === 'safeguard_hold' && p.rejection_code === 'PRICE_JUMP_AT_EXECUTE' && (
                <PriceJumpHoldActions purchase={p} order={order} onAction={onAction} />
              )}

              {/* Seguir precio/stock en Keepa — compras en espera (spec 2026-09-20) */}
              {['pending_approval', 'safeguard_hold', 'error'].includes(p.amazon_status) && (
                <Can cap="orders.price_watch">
                  <PriceWatchButton purchase={p} order={order} onAction={onAction} />
                </Can>
              )}

              {/* ASIN editable */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text-muted)] w-10">ASIN</span>
                {can('orders.edit_cost') ? (
                  <InlineEdit
                    value={p.asin || ''}
                    mono
                    placeholder="B0XXXXXXXX"
                    onSave={(val) => onAction('change_asin', order, { purchaseId: p.id, asin: val })}
                  />
                ) : (
                  <span className="text-xs font-mono text-[var(--color-text)]">{p.asin || '—'}</span>
                )}
              </div>

              {/* Cost editable */}
              <Can cap="global.view_money">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-text-muted)] w-10">Cost</span>
                  {can('orders.edit_cost') ? (
                    <InlineEdit
                      value={p.unit_cost != null ? String(p.unit_cost) : ''}
                      placeholder="0.00"
                      onSave={(val) =>
                        onAction('edit_cost', order, { purchaseId: p.id, cost: parseFloat(val) })
                      }
                    />
                  ) : (
                    <span className="text-xs text-[var(--color-text)]">{formatCurrency(p.unit_cost)}</span>
                  )}
                </div>
              </Can>

              {/* Total + profit */}
              <Can cap="global.view_money">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[var(--color-text-muted)]">
                    Total: <span className="text-[var(--color-text)]">{formatCurrency(p.total_cost)}</span>
                  </span>
                  {p.profit != null && (
                    <span
                      className={
                        p.profit >= 0
                          ? 'text-[var(--color-success)] font-medium'
                          : 'text-[var(--color-error)] font-medium'
                      }
                    >
                      {formatCurrency(p.profit)}
                      {p.profit_margin != null && ` (${p.profit_margin.toFixed(1)}%)`}
                    </span>
                  )}
                </div>
              </Can>

              {/* Picker de opciones de envío — compras frenadas por entrega lenta / tope de envío.
                  Oculto si el envío ML está retenido por cupo del comprador (Sin Cupo). */}
              {p.amazon_status === 'error' &&
                !order.shipments?.some(s => s.substatus === 'receiver_insufficient_quota') &&
                ['DELIVERY_SLOW', 'DELIVERY_SLOW_OPTIONS', 'SHIPPING_CAP_EXCEEDED'].includes(
                  p.rejection_code || ''
                ) && <ShippingOptionsPicker purchase={p} order={order} onAction={onAction} />}

              {/* Convert to internal stock — orden cancelada O atrasada (2026-08-28: el producto
                  volvió a nuestro poder) + Amazon delivered.
                  Ya convertida (flag del backend por movement conversion_to_internal): badge, sin botón. */}
              {(order.status === 'cancelled' || isDelayedShipment(order)) && p.amazon_status === 'delivered' && (
                (p as any).converted_to_stock ? (
                  <span
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700"
                    title="Esta unidad ya fue rescatada como stock interno"
                  >
                    <ArrowDownUp className="w-3 h-3" />
                    Convertido a Stock Interno ✓
                  </span>
                ) : (
                  <Can cap="orders.operate">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction('convert_to_internal', order, { purchase: p });
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-700 transition-colors"
                      title="Rescatar esta unidad como stock interno vendible"
                    >
                      <ArrowDownUp className="w-3 h-3" />
                      Convertir a Stock Interno
                    </button>
                  </Can>
                )
              )}

              {/* Purchase lifecycle tracker + tracking number */}
              {(p as any).events?.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap">
                  <PurchaseTracker
                    events={(p as any).events}
                    currentStatus={p.amazon_status}
                    compact
                    trackingNumber={p.tracking_number || undefined}
                    carrier={p.carrier || undefined}
                    unitCost={can('global.view_money') && p.unit_cost != null ? parseFloat(String(p.unit_cost)) : undefined}
                  />
                  {p.tracking_number && (
                    <span
                      className="text-[10px] font-mono text-[var(--color-text-muted)] break-all"
                      title={p.carrier ? `${p.carrier} ${p.tracking_number}` : p.tracking_number}
                    >
                      {p.carrier && <span className="opacity-70 mr-1">{p.carrier}</span>}
                      {p.tracking_number}
                    </span>
                  )}
                </div>
              )}

              {/* Tracking + carrier + delivery (fallback when no events) */}
              {!(p as any).events?.length && (p.tracking_number || p.carrier || p.delivery_date) && (
                <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] flex-wrap">
                  {p.carrier && <span>{p.carrier}</span>}
                  {p.tracking_number && (
                    <span className="font-mono break-all" title={p.tracking_number}>
                      {p.tracking_number}
                    </span>
                  )}
                  {p.delivery_date && (
                    <span>
                      {new Date(p.delivery_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              )}

              {/* PO reference */}
              {p.po_id && (
                <div className="text-xs text-[var(--color-primary)] bg-[var(--color-primary-light)] rounded px-2 py-1 inline-flex items-center gap-1">
                  <FileText size={11} />
                  {p.po_number || `PO #${p.po_id}`}
                  {p.po_status && <span className="opacity-60">({p.po_status})</span>}
                </div>
              )}
              {p.stock_po_id && (
                <div className="text-xs text-[var(--color-success)] bg-[var(--color-success-light)] rounded px-2 py-1 inline-flex items-center gap-1">
                  <Package size={11} />
                  Stock PO #{p.stock_po_id}
                </div>
              )}

              {/* Editar trackings (solo compras manuales no-Amazon) */}
              {p.link_source === 'generic' && (
                <Can cap="orders.operate">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction('edit_trackings', order, { purchaseId: p.id });
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
                    title="Agregar/editar trackings de esta compra manual"
                  >
                    <Truck size={11} />
                    Editar trackings
                  </button>
                </Can>
              )}

              {/* Error */}
              {p.error_message && (
                <p className="text-xs text-[var(--color-error)] bg-[var(--color-error-light)] rounded px-2 py-1">
                  {p.error_message}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Zone 3: MercadoLibre ────────────────────────────────── */

function MLZone({
  order,
  onAction,
}: {
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const { t } = useTranslation();
  const mlStatusColor = ML_STATUS_COLORS[order.status] || 'var(--color-text-muted)';

  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        MercadoLibre
      </h4>

      {/* Buyer */}
      <div className="mb-3">
        {/* Nombre real del comprador solo con la llave de datos de compradores; el apodo queda siempre. */}
        <Can
          cap="global.view_buyers"
          fallback={<p className="text-sm font-medium text-[var(--color-text)]">{order.buyer.name}</p>}
        >
          <p className="text-sm font-medium text-[var(--color-text)]">{order.buyer.full_name || order.buyer.name}</p>
          {order.buyer.full_name && (
            <p className="text-xs text-[var(--color-text-muted)]">{order.buyer.name}</p>
          )}
        </Can>
      </div>

      {/* Shipments */}
      {order.shipments.length > 0 && (
        <div className="space-y-2 mb-3">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            {t('orders.accordion.shipments', 'Shipments')}
          </span>
          {order.shipments.map((s) => (
            <ShipmentCard key={s.ml_shipment_id} shipment={s} />
          ))}
        </div>
      )}

      {/* Claims */}
      {order.claims.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            {t('orders.accordion.claims', 'Claims')}
          </span>
          {order.claims.map((c) => (
            <ClaimCard key={c.ml_claim_id} claim={c} onClick={() => onAction('open_claim', order, { claim: c })} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShipmentCard({ shipment }: { shipment: Shipment }) {
  return (
    <div className="p-2 rounded-[var(--border-radius)] border border-[var(--color-border-light)] bg-[var(--color-bg)] text-xs space-y-1">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[var(--color-text-muted)]">#{shipment.ml_shipment_id}</span>
        <span className="text-[var(--color-text-secondary)]">{shipment.status}</span>
      </div>
      {shipment.substatus && (
        <p className="text-[var(--color-text-muted)]">{shipment.substatus}</p>
      )}
      {shipment.shipping_method_name && (
        <p className="text-[var(--color-text-muted)]">{shipment.shipping_method_name}</p>
      )}
      {shipment.logistic_type && (
        <p className="text-[var(--color-text-muted)]">Type: {shipment.logistic_type}</p>
      )}
      {shipment.tracking_number && (
        <p className="font-mono text-[var(--color-text)]">{shipment.tracking_number}</p>
      )}
      {shipment.shipping_cost != null && (
        <Can cap="global.view_money">
          <p className="text-[var(--color-text-muted)]">
            Shipping: {formatCurrency(shipment.shipping_cost)}
          </p>
        </Can>
      )}
      {shipment.estimated_delivery_date && (
        <p className="text-[var(--color-text-muted)]">
          ETA:{' '}
          {new Date(shipment.estimated_delivery_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </p>
      )}
      {shipment.shipped_at && (
        <p className="text-[var(--color-text-muted)]">
          Shipped:{' '}
          {new Date(shipment.shipped_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </p>
      )}
      {shipment.delivered_at && (
        <p className="text-[var(--color-success)] font-medium">
          Entregado:{' '}
          {new Date(shipment.delivered_at).toLocaleDateString('es-AR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
          {' · '}
          {(() => {
            const days = Math.floor((Date.now() - new Date(shipment.delivered_at).getTime()) / 86400000);
            if (days <= 0) return 'hoy';
            if (days === 1) return 'hace 1 día';
            return `hace ${days} días`;
          })()}
        </p>
      )}
    </div>
  );
}

function ClaimCard({ claim, onClick }: { claim: Claim; onClick?: () => void }) {
  const title = claim.detail_title || `${claim.type} / ${claim.stage}`;
  const hasDueDate = claim.detail_due_date && claim.status === 'opened';

  return (
    <div
      className="p-2 rounded-[var(--border-radius)] border border-[var(--color-error-light)] bg-[var(--color-error-light)] text-xs space-y-1 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={12} className="text-[var(--color-error)]" />
        <span className="font-mono text-[var(--color-text)]">#{claim.ml_claim_id}</span>
        <span className="text-[var(--color-error)] font-medium">{claim.status}</span>
      </div>
      <p className="text-[var(--color-text)] font-medium">{title}</p>
      {claim.detail_problem && (
        <p className="text-[var(--color-text-muted)]">{claim.detail_problem}</p>
      )}
      {hasDueDate && (
        <p className="text-[var(--color-warning)] font-medium">
          ⏰ {new Date(claim.detail_due_date!).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </p>
      )}

      {/* Return tracking section */}
      {claim.return_id && (
        <div className="mt-2 pt-2 border-t border-[var(--color-border)] space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <RotateCcw size={12} style={{ color: claim.return_status === 'delivered' ? 'var(--color-success)' : 'var(--color-info)' }} />
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: claim.return_status === 'delivered' ? 'var(--color-success)' : 'var(--color-info)',
                color: 'white',
              }}
            >
              Devolucion: {claim.return_status ?? 'pendiente'}
            </span>
            {claim.return_status_money && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{
                  backgroundColor: claim.return_status_money === 'refunded' ? 'var(--color-success)' : 'var(--color-warning)',
                  color: 'white',
                }}
              >
                {claim.return_status_money === 'refunded' ? 'Reembolsado' : 'Dinero retenido'}
              </span>
            )}
          </div>

          {claim.return_tracking_number && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-muted)]">
              <Truck size={11} />
              <span className="font-mono">{claim.return_tracking_number}</span>
            </div>
          )}

          {/* Recibida en el depósito por escaneo de la guía (2026-09-23) */}
          {claim.return_received_at && (
            <div className="flex items-center gap-1.5" style={{ color: 'var(--color-success)' }}>
              <Package size={11} />
              <span>
                Recibida en el depósito el {new Date(claim.return_received_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                {claim.return_received_by ? ` (${claim.return_received_by})` : ''}
              </span>
            </div>
          )}

          {(claim.return_destination_city || claim.return_destination_state || claim.return_destination_country) && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-muted)]">
              <Package size={11} />
              <span>
                {[claim.return_destination_city, claim.return_destination_state, claim.return_destination_country]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          )}

          {claim.detail_description && (
            <p className="text-[var(--color-text-muted)] italic">{claim.detail_description}</p>
          )}

          {claim.return_refund_at && (
            <p className="text-[var(--color-text-muted)] text-[10px]">
              Reembolso: {claim.return_refund_at === 'delivered' ? 'al recibir el paquete' : 'al ser enviado'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Zone 4: Actions ─────────────────────────────────────── */

function ActionsZone({
  order,
  onAction,
}: {
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  // Envío retenido por cupo de importación del comprador: no ofrecer NINGUNA
  // vía de compra (approve/manual/genérica) hasta que ML libere el envío.
  const quotaHold = order.shipments?.some(s => s.substatus === 'receiver_insufficient_quota');
  const actions: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    variant?: 'primary' | 'default' | 'danger';
    show: boolean;
    /** Capacidades que habilita el botón: se piden TODAS. Lista vacía = siempre visible (solo abre ML). */
    caps: string[];
  }> = [
    {
      key: 'approve',
      caps: ['orders.buy'],
      label: t('orders.actions.approve', 'Approve'),
      icon: <Check size={14} />,
      variant: 'primary',
      show: !quotaHold && order.status !== 'cancelled' && order.purchases.some(
        (p) => p.amazon_status === 'pending_approval' || p.amazon_status === 'safeguard_hold'
      ),
    },
    {
      key: 'retry',
      caps: ['orders.buy'],
      label: t('orders.actions.retry', 'Retry'),
      icon: <RotateCcw size={14} />,
      show: order.status !== 'cancelled' && order.purchases.some(
        (p) => p.amazon_status === 'error' || p.amazon_status === 'failed'
      ),
    },
    {
      key: 'label',
      caps: ['orders.operate'],
      label: t('orders.actions.label', 'Label'),
      icon: <Truck size={14} />,
      show: order.shipments.some(s => s.status === 'ready_to_ship'),
    },
    {
      key: 'link',
      caps: ['orders.operate'],
      label: t('orders.actions.link', 'Link'),
      icon: <Link2 size={14} />,
      show: true,
    },
    {
      key: 'generic_purchase',
      // La Compra Manual escribe costo unitario y envío: pide operar Y ver dinero.
      caps: ['orders.operate', 'global.view_money'],
      label: t('orders.actions.generic_purchase', 'Compra Manual'),
      icon: <ShoppingBag size={14} />,
      show: !quotaHold && order.status === 'paid',
    },
    {
      key: 'create_po',
      // Crear una orden de compra sin ver costos cargaría costo cero: exige también ver dinero.
      caps: ['orders.operate', 'global.view_money'],
      label: t('orders.actions.createPO', 'Create PO'),
      icon: <FileText size={14} />,
      show: order.purchases.some((p) => !p.stock_po_id && p.amazon_status === 'ordered'),
    },
    {
      key: 'unlink',
      caps: ['orders.operate'],
      label: t('orders.actions.unlink', 'Desvincular'),
      icon: <Link2 size={14} />,
      variant: 'danger',
      show: order.purchases.some((p) => p.link_source === 'manual' && !['cancelled'].includes(p.amazon_status)),
    },
    {
      key: 'delete_purchase',
      caps: ['orders.delete_purchase'],
      label: t('orders.actions.deletePurchase', 'Eliminar compra'),
      icon: <Trash2 size={14} />,
      variant: 'danger',
      show: order.purchases.some(
        (p) => ['error', 'cancelled'].includes(p.amazon_status)
      ),
    },
    {
      key: 'reimburse',
      caps: ['orders.refund'],
      label: order.seller_reimbursed ? 'Desmarcar Reintegro' : 'Marcar Reintegrado',
      icon: order.seller_reimbursed ? <ShieldX size={14} /> : <ShieldCheck size={14} />,
      variant: order.seller_reimbursed ? 'danger' : 'primary',
      // Atrasados (2026-08-28): ML también reembolsa envíos en viaje con fecha vencida
      show:
        order.shipments.some(s => s.status === 'not_delivered' && (order.status === 'cancelled' || s.substatus === 'fraudulent')) ||
        (isDelayedShipment(order) && !order.delayed_loss_confirmed_at) ||
        (order.seller_reimbursed && order.status === 'paid'),
    },
    // Reclamo de no entregadas a ML (etapa 2): según el expediente vigente de la venta.
    {
      key: 'nd_claim_stop',
      caps: ['orders.refund'],
      label: 'No insistir',
      icon: <Ban size={14} />,
      variant: 'default',
      show: ndClaimActions(order.nd_claim).includes('nd_claim_stop'),
    },
    {
      key: 'nd_claim_retry',
      caps: ['orders.refund'],
      label: 'Volver a reclamar',
      icon: <RotateCcw size={14} />,
      variant: 'default',
      show: ndClaimActions(order.nd_claim).includes('nd_claim_retry'),
    },
    {
      key: 'returned_to_seller',
      caps: ['orders.refund'],
      label: order.returned_to_seller ? 'Desmarcar Devuelto' : 'Devuelto al Vendedor',
      icon: order.returned_to_seller ? <ShieldX size={14} /> : <Package size={14} />,
      variant: order.returned_to_seller ? 'danger' : 'primary',
      // Solo envíos que volvieron al vendedor (returned / returning_to_sender)
      show: order.shipments.some(
        s => s.status === 'not_delivered' && ['returned', 'returning_to_sender'].includes(s.substatus || '')
      ),
    },
    {
      key: 'confirm_loss',
      caps: ['orders.refund'],
      label: 'ML rechazó — confirmar pérdida',
      icon: <ShieldX size={14} />,
      variant: 'danger',
      show:
        (order.shipments.some(s => s.status === 'not_delivered' && (order.status === 'cancelled' || s.substatus === 'fraudulent')) ||
          (isDelayedShipment(order) && !order.delayed_loss_confirmed_at)) &&
        !order.seller_reimbursed,
    },
    {
      key: 'cancel_order',
      caps: ['orders.operate'],
      label: t('orders.actions.cancelOrder', 'Cancelar orden ML'),
      icon: <Ban size={14} />,
      variant: 'danger',
      show:
        order.status !== 'cancelled' &&
        !order.shipments.some((s) => ['shipped', 'delivered', 'not_delivered'].includes(s.status)),
    },
    {
      key: 'send_message',
      caps: ['orders.messages'],
      label: t('orders.actions.sendMessage', 'Enviar Mensaje'),
      icon: <MessageCircle size={14} />,
      show: order.status === 'paid',
    },
    {
      key: 'view_ml',
      caps: [],
      label: t('orders.actions.viewInML', 'View in ML'),
      icon: <ExternalLink size={14} />,
      show: true,
    },
  ];

  const variantClasses: Record<string, string> = {
    primary:
      'bg-[var(--color-primary)] text-white hover:opacity-90',
    default:
      'bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]',
    danger:
      'bg-[var(--color-error-light)] text-[var(--color-error)] hover:opacity-90',
  };

  return (
    <div>
      <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        {t('orders.accordion.actions', 'Actions')}
      </h4>
      <div className="flex flex-col gap-1.5">
        {actions
          .filter((a) => a.show && a.caps.every((c) => can(c)))
          .map((a) => (
            <button
              key={a.key}
              onClick={() => {
                if (a.key === 'view_ml') {
                  const id = order.pack_id || order.ml_order_id;
                  window.open(
                    `https://www.mercadolibre.com/ventas/${id}/detalle`,
                    '_blank'
                  );
                } else if (a.key === 'send_message') {
                  onAction('send_message', order);
                } else {
                  onAction(a.key, order);
                }
              }}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-[var(--border-radius)] transition-all w-full ${
                variantClasses[a.variant || 'default']
              }`}
            >
              {a.icon}
              {a.label}
            </button>
          ))}
      </div>

    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */

interface OrderAccordionProps {
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}

export function OrderAccordion({ order, onAction }: OrderAccordionProps) {
  const { t } = useTranslation();
  const sellerId = useUIStore(s => s.sellerId);
  const hasAmazon = order.purchases.length > 0;
  const hasStock = (order.internal_purchases || []).length > 0;

  return (
    <div className="bg-[var(--color-surface)] border-t border-[var(--color-border-light)]">
      {/* Bolsa WH (full) — bloque destacado al inicio del detalle */}
      {order.ml_order_id && (
        <div className="px-4 pt-4">
          <BagBadge mlOrderId={order.ml_order_id} sellerId={sellerId} variant="full" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
        <ItemsZone items={order.items} orderId={order.ml_order_id} />

      {/* Source column: Amazon, Stock, or both */}
      <div className="space-y-4">
        {hasAmazon && <AmazonZone purchases={order.purchases} order={order} onAction={onAction} />}
        {hasStock && <StockZone internalPurchases={order.internal_purchases} order={order} onAction={onAction} />}
        {!hasAmazon && !hasStock && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
              Source
            </h4>
            <p className="text-sm text-[var(--color-text-muted)] italic">
              {t('orders.accordion.noPurchases', 'No purchases linked')}
            </p>
          </div>
        )}
      </div>

        <MLZone order={order} onAction={onAction} />
        <ActionsZone order={order} onAction={onAction} />
      </div>
    </div>
  );
}
